import { useCallback, useEffect, useRef, useState } from "react";
import { getAttendanceHistory, identifyAttendanceSubject } from "@services/api";
import { currentMonthIST } from "@utils/attendanceCalendar";
import type {
  ApiErrorBody,
  AttendanceHistoryParams,
  HistoryResponse,
  IdentifyResponse,
} from "./types";

const GENERIC_ERROR = "Could not load attendance history. Try again.";

/**
 * Loads one month of history for the session's subject. On
 * SUBJECT_TOKEN_EXPIRED it re-identifies with the stored phone and retries
 * once. Responses for a month the user has already left are dropped.
 */
export const useAttendanceHistory = (session: AttendanceHistoryParams) => {
  const [month, setMonth] = useState(() => currentMonthIST());
  const [data, setData] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const tokenRef = useRef(session.identity.subject_token);
  const requestId = useRef(0);

  const fetchMonth = useCallback(
    async (target: string) => {
      requestId.current += 1;
      const mine = requestId.current;
      setLoading(true);
      setError(null);
      try {
        let res = await getAttendanceHistory({
          subjectToken: tokenRef.current,
          month: target,
        });
        const first = (res?.data ?? {}) as ApiErrorBody;
        if (res?.status === 401 && first.error === "SUBJECT_TOKEN_EXPIRED") {
          const fresh = await identifyAttendanceSubject({
            role: session.identity.role,
            phone: session.phone,
          });
          if (fresh?.status === 200 && fresh.data?.subject_token) {
            tokenRef.current = (fresh.data as IdentifyResponse).subject_token;
            res = await getAttendanceHistory({
              subjectToken: tokenRef.current,
              month: target,
            });
          }
        }
        if (mine !== requestId.current) return;
        if (res?.status === 200 && Array.isArray(res.data?.days)) {
          setData(res.data as HistoryResponse);
          return;
        }
        setData(null);
        setError(((res?.data ?? {}) as ApiErrorBody).message || GENERIC_ERROR);
      } catch (_e) {
        if (mine !== requestId.current) return;
        setData(null);
        setError(GENERIC_ERROR);
      } finally {
        if (mine === requestId.current) setLoading(false);
      }
    },
    [session.identity.role, session.phone],
  );

  useEffect(() => {
    fetchMonth(month);
  }, [month, fetchMonth]);

  const retry = useCallback(() => fetchMonth(month), [fetchMonth, month]);

  return { month, setMonth, data, loading, error, retry };
};
